import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { DocumentsUploader } from "@/components/DocumentsUploader";

const STEPS = ["Business", "Processing", "Corridor", "Documents"];
const JURIS = ["United States", "Mexico", "Bulgaria", "Amsterdam (NL)", "Other EU", "Other LATAM"];
const PRODUCTS = [
  ["plan_highrisk", "High-risk card processing"], ["term_countertop", "POS terminals"], ["setup_gateway", "Payment gateway"],
  ["plan_fx", "Cross-border FX settlement"], ["plan_ach", "ACH & eCheck"],
];
const INIT = { business_name: "", contact_name: "", email: "", phone: "", website: "", industry: "", country: "United States",
  years_in_business: "", monthly_volume: "", avg_ticket: "", chargeback_ratio: "", currently_processing: false, jurisdictions: ["United States"], products: [], notes: "" };

const F = ({ label, children }) => (
  <label className="block"><span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">{label}</span>{children}</label>
);

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${on ? "border-[var(--gold)] bg-[var(--gold)]/10 text-white" : "border-white/15 text-slate-300 hover:border-white/40"}`}>
    {on && <Check className="h-3.5 w-3.5 text-[var(--gold)]" />}{children}
  </button>
);

export default function Apply() {
  const [params] = useSearchParams();
  const pre = params.get("product");
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ ...INIT, products: pre ? [pre] : [] });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (k, v) => setF({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] });

  const valid = [
    f.business_name && f.contact_name && /\S+@\S+\.\S+/.test(f.email) && f.industry,
    f.monthly_volume,
    true,
  ];

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/applications", f);
      setDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <main className="mx-auto max-w-3xl px-5 pb-28 pt-36" data-testid="apply-success">
        <p className="eyebrow">Step 4 of 4 · Documents</p>
        <h1 className="mt-5 font-display text-4xl font-semibold tracking-tight sm:text-5xl">Application received.</h1>
        <p className="mt-5 text-slate-300">Reference <span className="font-mono text-[var(--gold)]" data-testid="apply-reference">{done.reference}</span>. Upload your documents now to speed up underwriting, or finish later using the private link we're emailing to {f.email}.</p>
        <div className="mt-10 border border-white/10 bg-[#0d1524] p-6 sm:p-8">
          <DocumentsUploader reference={done.reference} token={done.upload_token} />
        </div>
        <div className="mt-10 flex flex-wrap gap-3"><Link to="/" className="btn-primary" data-testid="apply-success-finish">I'm done</Link><Link to="/store" className="btn-ghost" data-testid="apply-success-store">Visit the storefront</Link></div>
      </main>
    );
  }

  return (
    <main className="mx-auto grid max-w-[1400px] gap-14 px-5 pb-28 pt-36 sm:px-10 lg:grid-cols-12" data-testid="apply-page">
      <aside className="lg:col-span-4">
        <p className="eyebrow">Merchant application</p>
        <h1 className="mt-5 font-display text-5xl font-semibold leading-[0.95] tracking-tight">Apply for processing.</h1>
        <p className="mt-6 text-slate-300">High-risk welcome. Three short steps; an underwriter reviews every file.</p>
        <ol className="mt-12 space-y-4">
          {STEPS.map((s, i) => (
            <li key={s} className="flex items-center gap-4" data-testid={`apply-step-indicator-${i}`}>
              <span className={`grid h-9 w-9 place-items-center rounded-full border font-mono text-xs ${i <= step ? "border-[var(--gold)] text-[var(--gold)]" : "border-white/15 text-slate-400"}`}>{i < step ? <Check className="h-4 w-4" /> : `0${i + 1}`}</span>
              <span className={i === step ? "text-white" : "text-slate-400"}>{s}</span>
            </li>
          ))}
        </ol>
      </aside>
      <section className="border border-white/10 bg-[#0d1524] p-6 sm:p-10 lg:col-span-8">
        <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45 }} className="grid gap-5 sm:grid-cols-2">
          {step === 0 && (<>
            <F label="Business name *"><input className="field" value={f.business_name} onChange={set("business_name")} data-testid="apply-business-name" /></F>
            <F label="Contact name *"><input className="field" value={f.contact_name} onChange={set("contact_name")} data-testid="apply-contact-name" /></F>
            <F label="Email *"><input className="field" type="email" autoComplete="off" value={f.email} onChange={set("email")} data-testid="apply-email" /></F>
            <F label="Phone"><input className="field" value={f.phone} onChange={set("phone")} data-testid="apply-phone" /></F>
            <F label="Website"><input className="field" value={f.website} onChange={set("website")} data-testid="apply-website" /></F>
            <F label="Industry / vertical *"><input className="field" placeholder="e.g. Nutraceuticals, travel, CBD" value={f.industry} onChange={set("industry")} data-testid="apply-industry" /></F>
            <F label="Country of incorporation"><input className="field" value={f.country} onChange={set("country")} data-testid="apply-country" /></F>
            <F label="Years in business">
              <select className="field" value={f.years_in_business} onChange={set("years_in_business")} data-testid="apply-years">
                <option value="">Select</option><option>Under 1</option><option>1-3</option><option>3-5</option><option>5+</option>
              </select>
            </F>
          </>)}
          {step === 1 && (<>
            <F label="Monthly card volume *">
              <select className="field" value={f.monthly_volume} onChange={set("monthly_volume")} data-testid="apply-volume">
                <option value="">Select</option><option>Under $50k</option><option>$50k - $250k</option><option>$250k - $1M</option><option>$1M - $5M</option><option>$5M+</option>
              </select>
            </F>
            <F label="Average ticket"><input className="field" placeholder="$" value={f.avg_ticket} onChange={set("avg_ticket")} data-testid="apply-avg-ticket" /></F>
            <F label="Current chargeback ratio"><input className="field" placeholder="%" value={f.chargeback_ratio} onChange={set("chargeback_ratio")} data-testid="apply-chargeback" /></F>
            <F label="Currently processing?">
              <div className="flex gap-2">
                <Chip on={f.currently_processing} onClick={() => setF({ ...f, currently_processing: true })} testid="apply-processing-yes">Yes</Chip>
                <Chip on={!f.currently_processing} onClick={() => setF({ ...f, currently_processing: false })} testid="apply-processing-no">No</Chip>
              </div>
            </F>
            <div className="sm:col-span-2"><F label="Products of interest">
              <div className="flex flex-wrap gap-2">{PRODUCTS.map(([id, l]) => <Chip key={id} on={f.products.includes(id)} onClick={() => toggle("products", id)} testid={`apply-product-${id}`}>{l}</Chip>)}</div>
            </F></div>
          </>)}
          {step === 2 && (<>
            <div className="sm:col-span-2"><F label="Where do you sell / need settlement?">
              <div className="flex flex-wrap gap-2">{JURIS.map((j) => <Chip key={j} on={f.jurisdictions.includes(j)} onClick={() => toggle("jurisdictions", j)} testid={`apply-juris-${j.split(" ")[0].toLowerCase()}`}>{j}</Chip>)}</div>
            </F></div>
            <div className="sm:col-span-2"><F label="Anything underwriting should know">
              <textarea rows={5} className="field" value={f.notes} onChange={set("notes")} data-testid="apply-notes" />
            </F></div>
            <p className="text-xs text-slate-400 sm:col-span-2">Next you'll upload your product pages, driver's license and credit check authorization.</p>
          </>)}
        </motion.div>
        <div className="mt-10 flex justify-between border-t border-white/10 pt-6">
          <button disabled={step === 0} onClick={() => setStep(step - 1)} className="btn-ghost disabled:opacity-30" data-testid="apply-back-button"><ArrowLeft className="h-4 w-4" /> Back</button>
          {step < 2 ? (
            <button disabled={!valid[step]} onClick={() => setStep(step + 1)} className="btn-primary" data-testid="apply-next-button">Continue <ArrowRight className="h-4 w-4" /></button>
          ) : (
            <button disabled={busy} onClick={submit} className="btn-primary" data-testid="apply-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit application</button>
          )}
        </div>
      </section>
    </main>
  );
}
